/* eslint-disable react/prop-types */

import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import { AiOutlineShop, AiOutlineTags, AiOutlineTag } from "react-icons/ai";
import { GoLocation } from "react-icons/go";
import getFilteredProductList from "lib/filters/products";
import { useLocation, useRouteMatch } from "react-router-dom";
import recommendationEngineUserAction from "lib/requests/recommendationEngine";
import FiltersSelectComponent from "./FiltersSelectComponent";
import FiltersNestedSelectComponent from "./FiltersNestedSelectComponent";
import FiltersSearchBoxComponent from "./FiltersSearchBoxComponent";
import { setFilters } from "./duck/slices";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: "#fafafa",
    flexGrow: "1",
    padding: "15px",
    "& > *": {
      marginBottom: "10px",
    },
  },
  hidden: {
    display: "none",
  },
}));

export default function ScenarioMapConstraintsComponent() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const filtersSlice = useSelector((state) => state.filters);
  const viewSlice = useSelector((state) => state.view);
  const recommendationEngineSlice = useSelector(
    (state) => state.recommendationEngine
  );
  const catalogData = useSelector((state) => state.catalog);
  const retailersData = useSelector((state) => state.retailers);
  const retailersGroupSlice = useSelector((state) => state.retailersGroup);
  const scenarioSelectedSlice = useSelector(
    (state) => state.scenarios.selectedScenario
  );
  const user = useSelector((state) => state.auth.user);
  const areasData = useSelector((state) => state.areas);
  const location = useLocation();
  const { url } = useRouteMatch();
  const isPricingOrTpo =
    location.pathname === url || location.pathname === `${url}/pricing`;

  const [filteredProducts, setFilteredProducts] = useState(
    catalogData.products
  );
  const [filteredAreas, setFilteredAreas] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState(null);
  const [filteredRetailers, setFilteredRetailers] = useState(null);
  const [filteredBrand, setFilteredBrand] = useState(null);
  const [filteredLines, setFilteredLines] = useState(null);
  const [filteredRetailerGroup, setFilteredRetailerGroup] = useState(null);
  const hideFilters =
    viewSlice === "summary" &&
    (location.pathname === `${url}` || location.pathname === `${url}/pricing`);

  const handleChange = (filtersObject) => {
    if (
      isPricingOrTpo &&
      filtersSlice.groupBy.key === "retailerGroup" &&
      filtersObject.retailerGroup &&
      filtersSlice.filters.retailer
    ) {
      dispatch(
        setFilters({
          ...filtersObject,
          retailer: "",
        })
      );
    }
    if (
      location.pathname === `${url}/competitors` &&
      filtersObject.retailer &&
      filtersSlice.filters.retailerGroup
    ) {
      dispatch(
        setFilters({
          ...filtersObject,
          retailerGroup: "",
        })
      );
    } else {
      dispatch(setFilters(filtersObject));
    }
  };

  useEffect(() => {
    let filteredProductCatalog = null;
    if (Object.keys(scenarioSelectedSlice).length !== 0) {
      // Use a Set to store unique product_ids
      const productIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique areas_ids
      const areaIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique lines_ids
      const categoriesIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique retailers_ids
      const retailersIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique brands_ids
      const brandIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique lines
      const linesIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique retailersGroup_ids
      const retailerGroupIdFromScenarioSelectedSet = new Set();

      // We use Sets because we are storing uniques and ids can repeat on each estimation
      Object.values(scenarioSelectedSlice.estimations).forEach((obj) => {
        productIdFromScenarioSelectedSet.add(obj.product_id);
        areaIdFromScenarioSelectedSet.add(obj.area_id);
        categoriesIdFromScenarioSelectedSet.add(obj.product.category_id);
        retailersIdFromScenarioSelectedSet.add(obj.retailer_id);
        brandIdFromScenarioSelectedSet.add(obj.product.brand_id);
        linesIdFromScenarioSelectedSet.add(obj.product.line_id);
        retailerGroupIdFromScenarioSelectedSet.add(
          obj.retailer.retailer_group_id
        );
      });
      // Filter the object based on keys in the Set
      // It returns an object with the product_id as key, and the product object inside it
      filteredProductCatalog = Object.entries(catalogData.products).reduce(
        (result, [key, value]) => {
          if (productIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        },
        {}
      );

      setFilteredAreas(
        Object.entries(areasData).reduce((result, [key, value]) => {
          if (areaIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        }, {})
      );

      setFilteredCategories(
        Object.entries(catalogData.categories).reduce(
          (result, [key, value]) => {
            if (categoriesIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
              // eslint-disable-next-line no-param-reassign
              result[key] = value;
            }
            return result;
          },
          {}
        )
      );

      setFilteredRetailers(
        Object.entries(retailersData).reduce((result, [key, value]) => {
          if (retailersIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        }, {})
      );

      setFilteredBrand(
        Object.entries(catalogData.brands).reduce((result, [key, value]) => {
          if (brandIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        }, {})
      );

      setFilteredLines(
        Object.entries(catalogData.lines).reduce((result, [key, value]) => {
          if (linesIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        }, {})
      );

      setFilteredRetailerGroup(
        Object.entries(retailersGroupSlice).reduce((result, [key, value]) => {
          if (retailerGroupIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        }, {})
      );
    }

    const productsForFilters = getFilteredProductList(
      filtersSlice.filters,
      filteredProductCatalog,
      // We should skip products because we want them all (after being filtered by catalog and brand) to appear in the dropdown
      { product: true }
    );

    // if (
    //   filtersSlice.filters.product &&
    //   !_.some(productsForFilters, (product) => {
    //     return !!Array.from(filtersSlice.filters.product).some(
    //       (obj) => obj.id === product.id
    //     );
    //   })
    // ) {
    //   dispatch(
    //     setFilters({
    //       ...filtersSlice.filters,
    //       product: undefined,
    //     })
    //   );
    // }

    setFilteredProducts(productsForFilters);
    const userActions = {};
    const differentFilters = [
      "product",
      "retailer",
      "line",
      "category",
      "area",
    ];
    differentFilters.forEach((item) => {
      if (filtersSlice.filters[item] !== undefined) {
        // This sould be changed to accept an array of ids instead of just one
        userActions[`${item}_id`] = filtersSlice.filters[item][0]?.id || null;
      } else {
        userActions[`${item}_id`] = null;
      }
    });

    if (!Object.values(userActions).every((x) => x === null || x === "")) {
      userActions.user_email =
        user !== undefined && user !== null ? user.username : null;
      // Get action id for filter recommendation
      const actionFilter = _.find(
        recommendationEngineSlice.recommendationActions,
        { label: "filters" }
      );
      if (actionFilter !== undefined) {
        userActions.action_id = actionFilter.id;
        recommendationEngineUserAction(userActions);
      }
    }
  }, [filtersSlice.filters, catalogData, scenarioSelectedSlice]);

  // changing from any groupBy to retailerGroup, remove retailer filter
  // useEffect(() => {
  //   if (filtersSlice.groupBy.key === "retailerGroup") {
  //     dispatch(setFilters({ retailer: undefined }));
  //   }
  // }, [filtersSlice.groupBy]);

  return (
    <div className={`${classes.root} ${hideFilters ? classes.hidden : ""}`}>
      {(location.pathname === `${url}` && viewSlice !== "summary") ||
      (location.pathname === `${url}/pricing` && viewSlice !== "summary") ? (
        <FiltersSearchBoxComponent handleChange={handleChange} />
      ) : (
        ""
      )}
      {location.pathname === `${url}/competitors` ? (
        <>
          <FiltersSelectComponent
            inputData={{
              key: "category",
              label: "Category",
              type: "normal",
              data: filteredCategories,
              icon: <AiOutlineTag size={22} />,
            }}
            filters={{ ...filtersSlice.filters, line: undefined }}
            sort="asc"
            handleChange={handleChange}
          />
        </>
      ) : (
        <FiltersNestedSelectComponent
          inputData={{
            key: "catalog",
            label: "Catalog",
            type: "nested",
            icon: <AiOutlineTags size={22} />,
            data: {
              parent: {
                data: filteredCategories,
                label: "Category",
                key: "category",
              },
              children: {
                label: "Line",
                key: "line",
                data: _.groupBy(filteredLines, (line) => line.categories[0].id),
              },
            },
          }}
          filters={filtersSlice.filters}
          sort="asc"
          handleChange={handleChange}
        />
      )}
      <FiltersSelectComponent
        inputData={{
          key: "brand",
          label: "Brand",
          type: "normal",
          data: filteredBrand,
          icon: <AiOutlineTag size={22} />,
        }}
        filters={filtersSlice.filters}
        sort="asc"
        handleChange={handleChange}
        disabled={location.pathname === `${url}/competitors`}
      />
      <FiltersSelectComponent
        inputData={{
          key: "product",
          label: "Product",
          type: "normal",
          data: filteredProducts,
          icon: <AiOutlineTag size={22} />,
        }}
        filters={filtersSlice.filters}
        sort="asc"
        handleChange={handleChange}
        disabled={location.pathname === `${url}/competitors`}
      />
      {location.pathname === `${url}/competitors` ||
      (location.pathname === `${url}` &&
        filtersSlice.groupBy.key === "retailerGroup") ||
      (location.pathname === `${url}/pricing` &&
        filtersSlice.groupBy.key === "retailerGroup") ? (
        <FiltersSelectComponent
          inputData={{
            key: "retailerGroup",
            label: "Retailers",
            type: "normal",
            data: Object.fromEntries(
              Array.from(
                new Set(
                  Object.entries(retailersData)
                    // eslint-disable-next-line no-unused-vars
                    .map(([key, value]) => value.retailer_groups)
                    .map(JSON.stringify)
                )
              ).map((item) => {
                const parsed = JSON.parse(item);
                return [parsed.id, parsed];
              })
            ),
            icon: <AiOutlineShop size={22} />,
          }}
          filters={filtersSlice.filters}
          sort="asc"
          disabled={
            location.pathname === `${url}/competitors` ||
            (isPricingOrTpo &&
              (filtersSlice.groupBy.key === "area" ||
                filtersSlice.groupBy.key === "product"))
          }
          handleChange={handleChange}
        />
      ) : (
        <FiltersNestedSelectComponent
          inputData={{
            key: "retailers",
            label: "Retailers",
            type: "nested",
            icon: <AiOutlineShop size={22} />,
            data: {
              parent: {
                data: filteredRetailerGroup,
                label: "RetailerGroup",
                key: "retailerGroup",
              },
              children: !(
                filtersSlice.groupBy.key === "retailerGroup" && isPricingOrTpo
              ) && {
                label: "Retailer",
                key: "retailer",
                data: _.groupBy(
                  filteredRetailers,
                  (retailer) => retailer.retailer_group_id
                ),
              },
            },
          }}
          filters={filtersSlice.filters}
          sort="asc"
          isPricingOrTpo={isPricingOrTpo}
          disabled={
            isPricingOrTpo &&
            (filtersSlice.groupBy.key === "area" ||
              filtersSlice.groupBy.key === "product")
          }
          handleChange={handleChange}
        />
      )}
      <FiltersSelectComponent
        inputData={{
          key: "area",
          label: "Area",
          type: "normal",
          data: filteredAreas,
          icon: <GoLocation size={22} />,
        }}
        filters={filtersSlice.filters}
        sort="asc"
        disabled={
          location.pathname === `${url}/competitors` ||
          (isPricingOrTpo &&
            (filtersSlice.groupBy.key === "retailer" ||
              filtersSlice.groupBy.key === "product" ||
              filtersSlice.groupBy.key === "retailerGroup"))
        }
        handleChange={handleChange}
      />
    </div>
  );
}
